import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/services/api";
import { toast } from "sonner";
import { Crown, Loader2, Gift, ChevronRight, Phone, ShieldCheck, Share2, ShoppingBasket, History, LogOut } from "lucide-react";
import { fmt, getSavedPhone, savePhone } from "../../lib/clubStore";

const TOKEN_KEY = "club_abuela_token";

export default function ClubAbuela() {
  const [config, setConfig] = useState(null);
  const [telefono, setTelefono] = useState(getSavedPhone());
  const [datos, setDatos] = useState(null);
  const [perfil, setPerfil] = useState(null);
  const [consultando, setConsultando] = useState(false);
  const [otpPaso, setOtpPaso] = useState(null);
  const [codigo, setCodigo] = useState("");
  const [enviandoOtp, setEnviandoOtp] = useState(false);
  const [error, setError] = useState("");
  const [cumple, setCumple] = useState({ dia: "", mes: "" });
  const [guardandoCumple, setGuardandoCumple] = useState(false);

  const guardarCumple = async () => {
    setGuardandoCumple(true);
    try {
      await api.put("/club/mi-cumpleanos", { dia: cumple.dia, mes: cumple.mes },
        { headers: { "X-Club-Token": localStorage.getItem(TOKEN_KEY) } });
      setPerfil((p) => ({ ...p, cumpleanos: { dia: cumple.dia, mes: cumple.mes } }));
      toast.success("¡Listo! La abuela te tendrá una sorpresa de cumpleaños 🎂");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No pudimos guardar tu cumpleaños");
    } finally {
      setGuardandoCumple(false);
    }
  };

  const cargarPerfil = useCallback(async (token) => {
    try {
      const r = await api.get("/club/mi-perfil", { headers: { "X-Club-Token": token } });
      setPerfil(r.data);
      setDatos(r.data);
      return true;
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      return false;
    }
  }, []);

  const consultar = useCallback(async (tel) => {
    const digits = (tel || "").replace(/\D/g, "");
    if (digits.length < 10) { setError("Escribe tu celular completo (10 dígitos)"); return; }
    setError("");
    setConsultando(true);
    try {
      const r = await api.get(`/club/consultar/${encodeURIComponent(tel)}`);
      setDatos(r.data);
      if (r.data.reconocido) savePhone(tel);
    } catch {
      setError("No pudimos consultar, intenta de nuevo");
    } finally {
      setConsultando(false);
    }
  }, []);

  useEffect(() => {
    api.get("/club/config").then((r) => setConfig(r.data));
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      cargarPerfil(token);
    } else {
      const saved = getSavedPhone();
      if (saved) consultar(saved);
    }
  }, [consultar, cargarPerfil]);

  const solicitarOtp = async () => {
    setEnviandoOtp(true);
    try {
      const r = await api.post("/club/otp/solicitar", { telefono });
      toast.success(r.data.message);
      setOtpPaso("codigo");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No pudimos enviar el código");
    } finally {
      setEnviandoOtp(false);
    }
  };

  const verificarOtp = async () => {
    setEnviandoOtp(true);
    try {
      const r = await api.post("/club/otp/verificar", { telefono, codigo });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      savePhone(telefono);
      setOtpPaso(null);
      setCodigo("");
      await cargarPerfil(r.data.token);
      toast.success("¡Número verificado! Bienvenido a tu mesa 👵");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Código incorrecto");
    } finally {
      setEnviandoOtp(false);
    }
  };

  const cerrarSesion = () => {
    localStorage.removeItem(TOKEN_KEY);
    setPerfil(null);
    setDatos(null);
  };

  const compartir = () => {
    const url = `${window.location.origin}/club`;
    const texto = encodeURIComponent(`👵 Te invito a la mesa de la abuela. En Baruj Shefa cada compra cuenta y desbloquea regalos. Mira el Club: ${url}`);
    window.open(`https://wa.me/?text=${texto}`, "_blank");
  };

  if (!config) return <div className="min-h-screen bg-[#FBF5E9] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#4A2C0A]" /></div>;

  return (
    <div className="min-h-screen bg-[#FBF5E9] pb-16" data-testid="club-abuela-page">
      {/* Hero */}
      <header className="relative text-amber-50 text-center overflow-hidden">
        {config.imagen_url ? (
          <>
            <img src={config.imagen_url} alt={config.nombre} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#2b1a06]/70 via-[#2b1a06]/75 to-[#2b1a06]/90" />
          </>
        ) : (
          <div className="absolute inset-0 bg-[#4A2C0A]" />
        )}
        <div className="relative max-w-md mx-auto px-5 pt-10 pb-14">
          <span className="text-5xl">👵</span>
          <h1 className="text-3xl sm:text-4xl font-bold mt-3" style={{ fontFamily: "Georgia, serif" }}>
            La abuela puede tener un regalo para ti
          </h1>
          <p className="text-sm text-amber-100 mt-2">Cada compra cuenta. Descubre tus beneficios y sigue sumando momentos alrededor de nuestra mesa.</p>
        </div>
      </header>

      <main className="relative z-10 max-w-md mx-auto px-5 -mt-8 space-y-6">
        {/* Consulta / Sesión */}
        {!perfil && (
          <div className="bg-white rounded-3xl shadow-lg border border-[#eee3cd] p-5">
            <p className="text-sm font-semibold text-[#4A2C0A] mb-2">Ingresa tu número de celular</p>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7a5e]" />
                <input
                  data-testid="club-telefono-input"
                  type="tel"
                  className="w-full pl-9 pr-3 py-3 rounded-xl border border-[#e2d5bc] text-sm text-[#4A2C0A] focus:outline-none focus:ring-2 focus:ring-[#C8902A]/50"
                  placeholder="Ej: 319 653 6874"
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && consultar(telefono)}
                />
              </div>
              <button data-testid="club-consultar-btn" onClick={() => consultar(telefono)} disabled={consultando}
                className="bg-[#C8902A] hover:bg-[#b07e22] text-white font-bold px-4 rounded-xl transition-colors disabled:opacity-60 text-sm">
                {consultando ? <Loader2 className="w-4 h-4 animate-spin" /> : "Descubrir"}
              </button>
            </div>
            {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
            <p className="text-[10px] text-[#8a7a5e] mt-2">Usamos tu celular solo para reconocer tus compras y beneficios. Tus datos están protegidos (Ley 1581 de 2012).</p>

            {datos && !datos.reconocido && (
              <div className="mt-3 bg-[#FBF5E9] rounded-xl p-4 text-center" data-testid="club-no-reconocido">
                <p className="text-base font-bold text-[#4A2C0A]" style={{ fontFamily: "Georgia, serif" }}>¡Bienvenido a la mesa de la abuela!</p>
                <p className="text-xs text-[#8a7a5e] mt-1">Con tu primera compra entras al club automáticamente y desbloqueas tu primer regalo: <strong>{config.niveles[0]?.recompensa?.descripcion}</strong></p>
                <Link to="/catalogo/baruj-principal" className="inline-block mt-3 bg-[#4A2C0A] text-amber-50 text-sm font-bold px-5 py-2.5 rounded-full">Hacer mi primera compra</Link>
              </div>
            )}

            {datos?.reconocido && !otpPaso && (
              <div className="mt-3 space-y-3">
                <div className="bg-[#FBF5E9] rounded-xl p-3 text-center">
                  <p className="text-sm text-[#4A2C0A]">¡Hola, <strong>{datos.nombre_pila}</strong>! {datos.nivel_actual ? `Ya eres ${datos.nivel_actual.emoji} ${datos.nivel_actual.nombre}.` : ""}</p>
                  {datos.beneficios?.some((b) => b.estado === "disponible") && (
                    <p className="text-xs text-[#C8902A] font-semibold mt-1">🎁 Tienes regalos esperándote</p>
                  )}
                </div>
                <button data-testid="club-verificar-btn" onClick={solicitarOtp} disabled={enviandoOtp}
                  className="w-full bg-[#4A2C0A] hover:bg-[#5d3a10] text-amber-50 font-bold py-3 rounded-full flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                  {enviandoOtp ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                  Verificar mi número y ver mis regalos
                </button>
                <p className="text-[10px] text-center text-[#8a7a5e]">Te enviaremos un código por SMS para proteger tu información</p>
              </div>
            )}

            {otpPaso === "codigo" && (
              <div className="mt-3 space-y-2" data-testid="club-otp-paso">
                <p className="text-xs text-[#4A2C0A] font-semibold text-center">Escribe el código de 6 dígitos que te llegó por SMS</p>
                <input
                  data-testid="club-otp-input"
                  inputMode="numeric" maxLength={6}
                  className="w-full text-center text-2xl tracking-[0.5em] font-bold py-3 rounded-xl border border-[#C8902A]/50 text-[#4A2C0A] focus:outline-none focus:ring-2 focus:ring-[#C8902A]/50"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ""))}
                />
                <button data-testid="club-otp-verificar-btn" onClick={verificarOtp} disabled={enviandoOtp || codigo.length !== 6}
                  className="w-full bg-[#C8902A] hover:bg-[#b07e22] text-white font-bold py-3 rounded-full transition-colors disabled:opacity-50">
                  {enviandoOtp ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Confirmar código"}
                </button>
                <button onClick={solicitarOtp} className="w-full text-xs text-[#8a7a5e]">Reenviar código</button>
              </div>
            )}
          </div>
        )}

        {/* Dashboard verificado */}
        {perfil?.verificado && (
          <>
            <div className="text-center bg-white rounded-2xl border border-[#eee3cd] shadow p-4">
              <p className="text-lg font-bold text-[#4A2C0A]" style={{ fontFamily: "Georgia, serif" }} data-testid="club-saludo">
                ¡Qué alegría tenerte de vuelta, {perfil.nombre_pila}!
              </p>
              <p className="text-xs text-[#8a7a5e]">Ya eres parte de nuestra familia y la abuela tiene sorpresas para ti.</p>
            </div>

            {/* Tarjeta de miembro */}
            <div className="relative bg-gradient-to-br from-[#4A2C0A] via-[#5d3a10] to-[#7a4d15] rounded-3xl p-5 text-amber-50 shadow-xl overflow-hidden" data-testid="club-tarjeta-miembro">
              <div className="absolute -right-6 -top-6 w-28 h-28 bg-[#C8902A]/20 rounded-full" />
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-[#C8902A]">Miembro del Club de la Abuela</p>
                  <p className="text-xl font-bold mt-0.5" style={{ fontFamily: "Georgia, serif" }}>{perfil.nombre || perfil.nombre_pila}</p>
                </div>
                <Crown className="w-6 h-6 text-[#C8902A]" />
              </div>
              {perfil.nivel_actual && (
                <p className="mt-2 inline-block bg-[#C8902A] text-white text-xs font-bold px-3 py-1 rounded-full">{perfil.nivel_actual.emoji} {perfil.nivel_actual.nombre}</p>
              )}
              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div><p className="text-lg font-bold">{perfil.compras}</p><p className="text-[9px] text-amber-200">Compras</p></div>
                <div><p className="text-lg font-bold">{fmt(perfil.acumulado)}</p><p className="text-[9px] text-amber-200">Acumulado</p></div>
                <div><p className="text-lg font-bold">{perfil.ultima_compra ? new Date(perfil.ultima_compra.slice(0, 10) + "T12:00:00").toLocaleDateString("es-CO", { day: "numeric", month: "short" }) : "—"}</p><p className="text-[9px] text-amber-200">Última compra</p></div>
              </div>
              <p className="text-[9px] text-amber-200/70 mt-3">{perfil.customer_id} · Miembro desde {(perfil.miembro_desde || "").slice(0, 10)}</p>
            </div>

            {/* Cumpleaños */}
            {!perfil.cumpleanos && (
              <div className="bg-white rounded-3xl shadow border border-[#eee3cd] p-4" data-testid="club-cumple-card">
                <p className="text-sm font-bold text-[#4A2C0A]">🎂 ¿Cuándo es tu cumpleaños?</p>
                <p className="text-[11px] text-[#8a7a5e] mb-2">Cuéntale a la abuela y recibe un regalo especial ese día</p>
                <div className="flex gap-2">
                  <input data-testid="cumple-dia-input" type="number" min="1" max="31" placeholder="Día"
                    className="w-20 px-3 py-2 rounded-xl border border-[#e2d5bc] text-sm text-[#4A2C0A] focus:outline-none focus:ring-2 focus:ring-[#C8902A]/50"
                    value={cumple.dia} onChange={(e) => setCumple({ ...cumple, dia: e.target.value })} />
                  <select data-testid="cumple-mes-select" value={cumple.mes} onChange={(e) => setCumple({ ...cumple, mes: e.target.value })}
                    className="flex-1 px-3 py-2 rounded-xl border border-[#e2d5bc] text-sm text-[#4A2C0A] bg-white focus:outline-none focus:ring-2 focus:ring-[#C8902A]/50">
                    <option value="">Mes</option>
                    {["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"].map((m, i) => (
                      <option key={m} value={i + 1}>{m}</option>
                    ))}
                  </select>
                  <button data-testid="cumple-guardar-btn" onClick={guardarCumple} disabled={guardandoCumple || !cumple.dia || !cumple.mes}
                    className="bg-[#C8902A] hover:bg-[#b07e22] text-white text-sm font-bold px-4 rounded-xl transition-colors disabled:opacity-50">
                    {guardandoCumple ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
                  </button>
                </div>
              </div>
            )}

            {/* Progreso */}
            {perfil.siguiente_nivel && perfil.progreso && (
              <div className="bg-white rounded-3xl shadow border border-[#eee3cd] p-4" data-testid="club-progreso">
                <div className="flex justify-between text-xs text-[#4A2C0A] mb-1.5">
                  <span className="font-semibold">Próximo nivel: {perfil.siguiente_nivel.emoji} {perfil.siguiente_nivel.nombre}</span>
                  <span>{perfil.progreso.compras_actuales}/{perfil.progreso.compras_meta} compras</span>
                </div>
                <div className="h-3 bg-[#e8dcc8] rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#C8902A] to-[#e0b050] rounded-full transition-all" style={{ width: `${perfil.progreso.pct}%` }} />
                </div>
                <p className="text-xs text-[#8a7a5e] mt-2">
                  {perfil.progreso.faltan_compras === 1
                    ? `¡Solo te falta 1 compra para llegar a ${perfil.siguiente_nivel.nombre}!`
                    : `Te faltan ${perfil.progreso.faltan_compras} compras (o ${fmt(perfil.siguiente_nivel.min_facturacion)} acumulados) para: ${perfil.siguiente_nivel.recompensa_descripcion}`}
                </p>
              </div>
            )}

            {/* Beneficios */}
            {perfil.beneficios?.length > 0 && (
              <div className="bg-white rounded-3xl shadow border border-[#eee3cd] p-4">
                <p className="text-sm font-bold text-[#4A2C0A] mb-2 flex items-center gap-1.5"><Gift className="w-4 h-4 text-[#C8902A]" /> Mis regalos</p>
                <div className="space-y-2">
                  {perfil.beneficios.map((b) => (
                    <div key={b.id} className={`rounded-2xl border p-3 ${b.estado === "disponible" ? "bg-amber-50 border-[#C8902A]/50" : "bg-gray-50 border-gray-200 opacity-70"}`} data-testid={`beneficio-${b.codigo}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-[#4A2C0A]">{b.nivel_emoji} {b.recompensa.descripcion}</p>
                          <p className="text-[11px] text-[#8a7a5e]">Código <strong>{b.codigo}</strong>{b.fecha_vencimiento ? ` · vence ${b.fecha_vencimiento.slice(0, 10)}` : ""}</p>
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${b.estado === "disponible" ? "bg-green-600 text-white" : "bg-gray-300 text-gray-700"}`}>
                          {b.estado === "disponible" ? "DISPONIBLE" : b.estado.toUpperCase()}
                        </span>
                      </div>
                      {b.estado === "disponible" && (
                        <p className="text-[11px] text-green-700 mt-1.5">Ingresa el código al comprar en el catálogo, o muéstraselo a tu vendedor.</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Acciones */}
            <div className="grid grid-cols-2 gap-3">
              <Link to="/catalogo/baruj-principal" data-testid="club-comprar-btn" className="bg-[#4A2C0A] hover:bg-[#5d3a10] text-amber-50 text-center text-sm font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2 transition-colors">
                <ShoppingBasket className="w-4 h-4" /> Comprar de nuevo
              </Link>
              <button data-testid="club-compartir-btn" onClick={compartir} className="bg-[#C8902A] hover:bg-[#b07e22] text-white text-sm font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2 transition-colors">
                <Share2 className="w-4 h-4" /> Invitar a la mesa
              </button>
            </div>

            {/* Historial */}
            {perfil.historial?.length > 0 && (
              <div className="bg-white rounded-3xl shadow border border-[#eee3cd] p-4" data-testid="club-historial">
                <p className="text-sm font-bold text-[#4A2C0A] mb-2 flex items-center gap-1.5"><History className="w-4 h-4" /> Mis últimas compras</p>
                {perfil.historial.map((h, i) => (
                  <div key={i} className="flex justify-between items-center text-xs py-2 border-b last:border-0 border-[#eee3cd]">
                    <div>
                      <p className="font-semibold text-[#4A2C0A]">{h.numero_pedido} <span className="font-normal text-[#8a7a5e]">· {String(h.fecha_creacion).slice(0, 10)}</span></p>
                      <p className="text-[#8a7a5e]">{h.productos}</p>
                    </div>
                    <p className="font-bold text-[#4A2C0A]">{fmt(h.total)}</p>
                  </div>
                ))}
              </div>
            )}

            <p className="text-center text-xs text-[#8a7a5e] italic">"Gracias por mantener viva nuestra tradición" — Abuela Marina</p>
            <button onClick={cerrarSesion} data-testid="club-salir-btn" className="w-full text-xs text-[#8a7a5e] flex items-center justify-center gap-1 py-1"><LogOut className="w-3 h-3" /> Salir de mi cuenta</button>
          </>
        )}

        {/* Niveles del club */}
        {!perfil && (
          <div>
            <h2 className="text-lg font-bold text-[#4A2C0A] mb-3 text-center" style={{ fontFamily: "Georgia, serif" }}>Así funciona el club</h2>
            <div className="space-y-3">
              {config.niveles.map((n, idx) => (
                <div key={n.id} className="bg-white rounded-2xl border border-[#eee3cd] p-4 flex items-start gap-3" data-testid={`nivel-card-${n.id}`}>
                  <span className="text-3xl">{n.emoji}</span>
                  <div className="flex-1">
                    <p className="font-bold text-[#4A2C0A]">{idx + 1}. {n.nombre}</p>
                    <p className="text-xs text-[#8a7a5e]">Con {n.min_compras} {n.min_compras === 1 ? "compra" : "compras"} o {fmt(n.min_facturacion)} acumulados</p>
                    <p className="text-xs text-[#C8902A] font-semibold mt-1">🎁 {n.recompensa.descripcion}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-center text-[11px] text-[#8a7a5e] mt-4">Tus compras en la tienda, con vendedores o por WhatsApp suman al mismo club usando tu celular. {config.descripcion ? "" : ""}Tradición que se disfruta hasta el final.</p>
          </div>
        )}
      </main>
    </div>
  );
}
